// scanVideoQueue.js — the upload queue, ABOVE the screen that started it.
// ---------------------------------------------------------------------------
// v0.207.4. Brett: "can these uploads work in the back ground so i can close
// the screen and it still uploads?"
//
// They could not, and the reason was structural rather than clever: the queue
// was React state inside ScanVideosModal, so closing the picker unmounted the
// only thing that knew an upload was happening. Half a floor scan died every
// time somebody tapped the X — and nothing said so, which is the part that
// makes it a fault rather than a limitation.
//
// So the queue is a MODULE, not a component. It outlives every screen, it is
// subscribed to rather than owned, and the picker is now a window onto it.
//
// ── WHAT "BACKGROUND" HONESTLY MEANS HERE ────────────────────────────────
// Close the picker, walk to another screen, drop a pin: the upload keeps
// going. Close the BROWSER or lock the phone and it stops — a backgrounded tab
// is suspended within seconds on iOS, and the Background Fetch API that would
// survive that is Chrome-on-Android only. Nobody should be told otherwise.
//
// What happens instead is that the clip is kept on the device
// (scanVideoStash.js) and the queue picks it up again on the next visit, with
// no camera roll to go back to — which is the answer to "will it start
// uploading again when the screen opens again". Yes, by itself.
//
// ── ONE AT A TIME, AND WHY ───────────────────────────────────────────────
// Three 250 MB clips in parallel on site reception finish later than three in a
// row, and the progress bar stops meaning anything while they do.
//
// ── A FACTORY, so it can be tested ───────────────────────────────────────
// createUploadQueue() takes its transport and its stash, so the whole thing —
// ordering, retry, resume, what survives — runs in a test with no network and
// no IndexedDB. The singleton below is that factory wired to the real ones.

import { uploadScanVideo, checkVideoFile } from './scanVideoUpload.js';

/** How long the device keeps an unfinished clip before letting it go. */
export const RESUME_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function createUploadQueue({ upload, stash, onChange }) {
  let items = [];          // [{ key, file, name, size, projectId, reportId, stage, pct, error, detail, persisted }]
  let notes = [];          // plain statements of fact — "that clip was already on the job"
  let running = false;
  const listeners = new Set();
  const noteListeners = new Set();

  const emit = () => {
    const snapshot = items.map((x) => ({ ...x }));
    listeners.forEach((fn) => { try { fn(snapshot); } catch (_) { /* a listener must never break an upload */ } });
    if (onChange) { try { onChange(snapshot); } catch (_) {} }
  };

  const emitNotes = () => {
    const snapshot = notes.slice();
    noteListeners.forEach((fn) => { try { fn(snapshot); } catch (_) {} });
  };

  const note = (text) => { notes = [...notes, { id: `${Date.now()}:${Math.random()}`, text }]; emitNotes(); };

  const patch = (key, next) => {
    items = items.map((x) => (x.key === key ? { ...x, ...next } : x));
    emit();
  };

  async function runNext() {
    if (running) return;
    const entry = items.find((x) => x.stage === 'queued');
    if (!entry) return;
    running = true;
    try {
      patch(entry.key, { stage: 'checking', pct: 0, error: null, detail: null });
      const out = await upload({
        file: entry.file,
        projectId: entry.projectId,
        reportId: entry.reportId,
        onStage: (stage, info) => patch(entry.key, { stage, pct: (info && info.pct) != null ? info.pct : undefined }),
      });
      if (out && out.ok) {
        // A clip the job already had is a success with nothing uploaded. Worth
        // saying once, because he picked it a second time for a reason and
        // silence would read as the pick having been ignored.
        if (out.duplicate) note(`“${entry.name}” was already on this job — it has not been added twice.`);
        // THE CLIP IS ON THE JOB. Only now is it safe to stop carrying it.
        await stash.drop(entry.key);
        items = items.filter((x) => x.key !== entry.key);
        emit();
      } else {
        patch(entry.key, { stage: 'error', error: (out && out.error) || 'The upload did not finish.', detail: (out && out.detail) || null });
      }
    } catch (e) {
      // A throw here would leave `running` true and the queue dead for the rest
      // of the session, with every later clip sitting at "queued" for ever.
      patch(entry.key, { stage: 'error', error: 'The upload did not finish.', detail: (e && e.message) || String(e) });
    } finally {
      running = false;
    }
    // Tail call rather than a loop: each clip re-enters cleanly, and a retry
    // added while this one ran is picked up in order.
    if (items.some((x) => x.stage === 'queued')) runNext();
  }

  return {
    subscribe(fn) {
      listeners.add(fn);
      try { fn(items.map((x) => ({ ...x }))); } catch (_) {}
      return () => listeners.delete(fn);
    },

    get() { return items.map((x) => ({ ...x })); },

    subscribeNotes(fn) {
      noteListeners.add(fn);
      try { fn(notes.slice()); } catch (_) {}
      return () => noteListeners.delete(fn);
    },

    clearNote(id) { notes = notes.filter((n) => n.id !== id); emitNotes(); },

    /** True while anything is queued or in flight — what the bar and the
     *  leaving-the-page warning both read. */
    busy() { return items.some((x) => x.stage !== 'error'); },

    async add(files, { projectId, reportId } = {}) {
      const list = Array.from(files || []);
      for (const file of list) {
        const key = `${file.name}:${file.size}:${Date.now()}:${Math.random()}`;
        const pre = checkVideoFile(file);
        if (!pre.ok) {
          // No file kept: there is nothing to retry, it is not a video.
          items = [...items, { key, name: file.name, size: file.size, stage: 'error', error: pre.reason }];
          emit();
          continue;
        }
        // KEPT BEFORE IT IS QUEUED. A clip that starts uploading and is not on
        // the device yet is a clip that vanishes if the app closes mid-upload,
        // which is the case this whole release exists for.
        const persisted = await stash.put({ key, file, projectId, reportId });
        items = [...items, {
          key, file, name: file.name, size: file.size,
          projectId, reportId, stage: 'queued', pct: 0, persisted,
        }];
        emit();
      }
      runNext();
    },

    retry(key) {
      const entry = items.find((x) => x.key === key);
      if (!entry || !entry.file) return;
      patch(key, { stage: 'queued', pct: 0, error: null, detail: null });
      runNext();
    },

    retryAll() {
      const keys = items.filter((x) => x.stage === 'error' && x.file).map((x) => x.key);
      if (!keys.length) return;
      items = items.map((x) => (keys.includes(x.key) ? { ...x, stage: 'queued', pct: 0, error: null, detail: null } : x));
      emit();
      runNext();
    },

    async dismiss(key) {
      await stash.drop(key);
      items = items.filter((x) => x.key !== key);
      emit();
    },

    /**
     * Pick up whatever the device is still carrying. Called once on start.
     *
     * The clips go back in as QUEUED, not as errors: nothing went wrong, the
     * app was closed. The API's own resume rule (v0.207.3) means a clip that
     * already has a row continues on THAT row rather than making a second one,
     * so this cannot produce the duplicates that started all of it.
     */
    async resume({ now = Date.now(), maxAgeMs = RESUME_MAX_AGE_MS } = {}) {
      const rows = await stash.list();
      const known = new Set(items.map((x) => x.key));

      // A CLIP IS NOT CARRIED FOR EVER. One that can never succeed would
      // otherwise retry on every app open, and a phone would hold 400 MB of it
      // indefinitely with nothing saying why the storage is gone. A week is
      // long enough to cover a job with no signal and short enough that the
      // device is not a graveyard.
      const stale = rows.filter((r) => r && r.key && (now - (r.createdAt || now)) > maxAgeMs);
      for (const r of stale) await stash.drop(r.key);
      const staleKeys = new Set(stale.map((r) => r.key));

      const restored = rows
        .filter((r) => r && r.key && r.file && !known.has(r.key) && !staleKeys.has(r.key))
        .map((r) => ({
          key: r.key, file: r.file, name: r.name || (r.file && r.file.name) || 'Video',
          size: r.size || (r.file && r.file.size) || 0,
          projectId: r.projectId, reportId: r.reportId,
          stage: 'queued', pct: 0, persisted: true, resumed: true,
        }));
      if (!restored.length) return 0;
      items = [...items, ...restored];
      emit();
      runNext();
      return restored.length;
    },
  };
}

// ── the one the app uses ───────────────────────────────────────────────────
// Imported lazily inside the wiring so a test of the factory never pulls in
// api.js, IndexedDB or XHR.

let _singleton = null;

export function scanVideoQueue() {
  if (_singleton) return _singleton;
  _singleton = createUploadQueue({
    upload: async (args) => {
      const { apiSend } = await import('../api.js');
      const { putToBlob } = await import('./putToBlob.js');
      return uploadScanVideo({
        ...args,
        post: (path, body) => apiSend('POST', path, body),
        patch: (path, body) => apiSend('PATCH', path, body),
        put: putToBlob,
      });
    },
    stash: {
      put: async (e) => (await import('./scanVideoStash.js')).stashClip(e),
      list: async () => (await import('./scanVideoStash.js')).listStash(),
      drop: async (k) => (await import('./scanVideoStash.js')).dropStash(k),
    },
  });
  return _singleton;
}
